import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import T from 'prop-types';
import { has, isEmpty, isFunction, times } from 'lodash-es';

import CollapsibleExclusionsList from './CollapsibleExclusionsList';
import WarningNotification from '../WarningNotification';
import Tooltip from '../../../Tooltip';
import Skeleton from '../../../Skeleton';
import MediaObject from '../../../MediaObject';
import SearchComponent from '../../../SearchComponent';
import FinanceMenuItems from '../../../TypeaheadItems/FinanceMenuItems';
import FinanceMenuFooter from '../../../TypeaheadItems/FinanceMenuFooter';
import { AppItemWithPerformance, PublisherItemWithPerformance } from '../../../TypeaheadItems';
import useMarketData from '../../hook/useMarketData';
import { useRefValue } from '../../../../hooks';

import { DATA_TYPE } from '../../configs/constants';
import { assetMapping, isUnified, restrictedFinanceAssets, truncateTicker } from '../../../../utils/helpers';
import { enhanceEntity, replaceEntity } from '../../../../utils/entities-replace';

const entitiesLimit = 50;

const baseSearchConfig = {
  assets: ['apps', 'publishers', 'companies', 'markets'],
  advancedRequestConfig: {
    markets: {
      endpoint: '/my_markets/search.json',
      params: {},
      allowEmptySearch: true,
    },
  },
  decorators: ['dls_30d', 'primary_category_id', 'category_ids', 'price', 'dls_tier_30d', 'apps_count', 'icons'],
  requestOptions: { decorate_recent_searches: true },
};

const advancedSearchConfig = {
  assets: ['apps'],
  decorators: ['dls_30d', 'primary_category_id', 'category_ids', 'price'],
  requestOptions: {
    decorate_recent_searches: true,
  },
};

const mpiSearchConfig = {
  assets: ['companies'],
  decorators: ['mpi_search'],
  requestOptions: {
    decorate_recent_searches: true,
  },
};

const loadingEntitiesSkeleton = times(5, i => (
  <div key={i} className="entity-item">
    <div className="d-flex align-items-center">
      <Skeleton width={12} height={12} className="mr-xxs" />
      <Skeleton width={16} height={16} className="mr-xxxs" />
      <Skeleton width={80} height={12} className="ml-xxxs" />
    </div>
    <Skeleton width={14} height={14} className="ml-auto" />
  </div>
));

const loadingEntitiesContainerSkeleton = (
  <div className="entities-container">
    <div className="entities-list">
      {loadingEntitiesSkeleton}
    </div>
  </div>
);

const decorateAppName = ({ name, bloomberg_ticker }) => !bloomberg_ticker ? name : (
  <>
    <span>{name}</span>
    <span className="app-name-ticker text-uppercase">({truncateTicker(bloomberg_ticker)})</span>
  </>
);

const searchKeys = {
  [DATA_TYPE.BASE]: 'base',
  [DATA_TYPE.ADVANCED]: 'advanced',
  [DATA_TYPE.MPI]: 'mpi',
};

const SearchEntities = ({
  initParams,
  formMode,
  dataType,
  report,
  autoFocus,
  prefetchingEntities = false,
  onReportChange,
  onReset,
}) => {
  const { permissions: { isLimitedFinanceCustomer } } = initParams;

  const searchComponentRef = useRef();
  const isLimitReached = useRef();

  const isAdvancedDataTypeRef = useRefValue(dataType === DATA_TYPE.ADVANCED);
  const isMpiDataTypeRef = useRefValue(dataType === DATA_TYPE.MPI);

  const searchConfigByDataType = useMemo(() => ({
    [DATA_TYPE.BASE]: baseSearchConfig,
    [DATA_TYPE.ADVANCED]: {
      ...advancedSearchConfig,
      requestOptions: {
        ...advancedSearchConfig.requestOptions,
        panels: [report.panel_version],
      },
    },
    [DATA_TYPE.MPI]: mpiSearchConfig,
  }), [report.panel_version]);

  const searchConfig = searchConfigByDataType[dataType];

  isLimitReached.current = report.reportEntities?.length >= entitiesLimit;

  const cleanupTypeahead = useCallback(({ clear = true, query, hide, toggle, focus } = {}) => {
    if (searchComponentRef.current) {
      const searchTypeahead = searchComponentRef.current.searchTypeaheadRef();
      if (clear) searchTypeahead.clear();
      if (query) searchTypeahead.setState({ text: query });
      if (hide) searchTypeahead.hideMenu();
      if (toggle) searchTypeahead.toggleMenu();
      if (focus) searchTypeahead.focus();
    }
  }, []);

  const handleReportChange = useCallback((nextEntities) => {
    onReportChange((prevState) => {
      const entities = isFunction(nextEntities) ? nextEntities(prevState.entities) : nextEntities;
      const appsOnlySelected = entities.length > 0
        && entities.every(item => assetMapping.apps.includes(item.kind));

      return ({
        ...prevState,
        entities,
        ...(isMpiDataTypeRef.current ? {} : {
          data_schema: appsOnlySelected ? 'app_unified' : 'parent_company_unified',
          split_by_acquisition: !isAdvancedDataTypeRef.curent && appsOnlySelected,
        }),
      });
    });
  }, []);

  const [{ loading, memoDataByMarket }, fetchMarketData] = useMarketData({
    onSuccess: handleReportChange,
  });

  const onEntitySelected = useCallback((entity) => {
    if (entity.kind === 'market') {
      const memoMarketData = memoDataByMarket.current[entity.id];
      const reportEntitiesRefNos = new Set(report.entities.map(({ ref_no }) => `${ref_no}`));

      return ({
        itemSelected: (!!memoMarketData && memoMarketData.every(({ ref_no }) => reportEntitiesRefNos.has(`${ref_no}`))),
      });
    }

    if (isMpiDataTypeRef.current && entity.kind === 'company' && !entity.has_mpi) {
      return {
        itemSelected: true,
      };
    }

    return enhanceEntity(report.entities)(entity);
  }, [report.entities]);

  const handleSelectEntity = useCallback((selectedEntity, query) => {
    if (selectedEntity.kind === 'market') {
      fetchMarketData(selectedEntity.id);
    } else {
      const isSelected = entities => entities.some(item => `${item.ref_no}` === `${selectedEntity.ref_no}`);
      const isSelectionDisabled = entities => isLimitReached.current || isSelected(entities);

      handleReportChange(entities => (
        isSelectionDisabled(entities) ? entities : [...entities, selectedEntity]
      ));
    }

    cleanupTypeahead({ query });
  }, []);

  const handleDeleteEntity = ({ ref_no }) => {
    handleReportChange(entites => entites.filter(entity => `${entity.ref_no}` !== `${ref_no}`));
  };

  const handleReset = (reportId) => {
    cleanupTypeahead();
    onReset(reportId);
  };

  const handleEntityReplace = useCallback((entity) => {
    handleReportChange(entities => replaceEntity(entity, entities));

    cleanupTypeahead({ clear: false, hide: true });
  }, []);

  const AppItemProps = useMemo(() => ({
    component: AppItemWithPerformance,
  }), []);

  const PublisherItemProps = useMemo(() => ({
    component: PublisherItemWithPerformance,
  }), []);

  // Focus search field programmatically
  useEffect(() => {
    if (formMode.isCreate && !isLimitReached.current && autoFocus) {
      // Using setTimeout as a workaround to ensure the desired behavior.
      setTimeout(() => cleanupTypeahead({ focus: true }), 100);
    }
  }, [dataType]);

  const customMenuItems = (asset) => {
    if (isLimitedFinanceCustomer && restrictedFinanceAssets.includes(asset)) {
      return ({ items, selectedAsset, renderItem }) => (
        <FinanceMenuItems
          items={items}
          selectedAsset={selectedAsset}
          renderItem={renderItem}
        />
      );
    }

    return undefined;
  };

  const noAppsSelected = (report.reportEntities?.length === 0 && !prefetchingEntities);

  const customMenuFooter = (asset) => {
    if (isLimitedFinanceCustomer && restrictedFinanceAssets.includes(asset)) {
      return <FinanceMenuFooter asset={asset} />;
    }

    return undefined;
  };

  const renderSelectedEntity = entity => (
    <div key={entity.ref_no} className="entity-item">
      {isMpiDataTypeRef.current && entity.kind === 'company' && !has(entity, 'has_mpi') && (
        <div className="entity-item__overlay" />
      )}
      <MediaObject
        {...entity}
        disableLink
        disablePublisherLink
        withStoreIcon
        appIconBordered
        noPublisherData
        appIconSize={16}
        showPubStore={entity.kind === 'publisher' && !isUnified(entity.store)}
        defaultBrokenNoStoreIcon={entity.kind === 'company' ? 'hicon-company' : undefined}
        decorateName={() => decorateAppName(entity)}
      />
      <i className="cursor-pointer hicon-close" onClick={() => handleDeleteEntity(entity)} />
    </div>
  );

  return (
    <section className="upsert-export-form__search-entities">
      <div className="upsert-export-form__search-entities-title">
        <h4 className="h5">Entities</h4>
      </div>
      <Tooltip content={isLimitReached.current ? `Exports are limited to ${entitiesLimit} entities.` : null}>
        <div className="search-entities-container">
          <SearchComponent
            key={searchKeys[dataType]}
            ref={searchComponentRef}
            availableAssets={searchConfig.assets}
            defaultSelectedAsset={searchConfig.assets[0]}
            decorators={searchConfig.decorators}
            requestOptions={searchConfig.requestOptions}
            advancedRequestConfig={searchConfig.advancedRequestConfig}
            placeholder="Search entities by name"
            minLength={0}
            maxHeight={isLimitedFinanceCustomer ? '570px' : undefined}
            disableRecentResults={false}
            disableSearchOnFocus={false}
            userPresent
            disableOnEnterDown
            disabled={isLimitReached.current}
            AppItemProps={AppItemProps}
            PublisherItemProps={PublisherItemProps}
            customMenuItems={customMenuItems}
            renderCustomMenuFooter={customMenuFooter}
            itemSelected={onEntitySelected}
            onReplaceClick={handleEntityReplace}
            onChange={handleSelectEntity}
          />
        </div>
      </Tooltip>
      {noAppsSelected && (
        <WarningNotification
          text="At least one entity must be selected"
          className="mt-mdl"
        />
      )}
      {loading && loadingEntitiesContainerSkeleton}
      {(report.entities?.length > 0 && !loading) && (
        <>
          <div className="entities-container">
            {(!isEmpty(report.reportEntities) || prefetchingEntities) && (
              <div className="entities-list">
                {report.reportEntities?.map(entity => renderSelectedEntity(entity))}
                {prefetchingEntities && loadingEntitiesSkeleton}
              </div>
            )}
            {!isEmpty(report.excludedEntities) && (
              <CollapsibleExclusionsList
                count={report.excludedEntities.length}
                tooltipText="Entities that do not have advanced data, or do not fit your current datapoint or preference selections will not be included in this export."
              >
                <div className="entities-list">
                  {report.excludedEntities.map(entity => renderSelectedEntity(entity))}
                </div>
              </CollapsibleExclusionsList>
            )}
          </div>
          <span className="clear-all-entities" onClick={() => handleReset(report.id)}>
            Clear all entities
          </span>
        </>
      )}
    </section>
  );
};

SearchEntities.propTypes = {
  dataType: T.string.isRequired,
  initParams: T.object.isRequired,
  formMode: T.object,
  report: T.object.isRequired,
  autoFocus: T.bool,
  prefetchingEntities: T.bool,
  onReset: T.func.isRequired,
  onReportChange: T.func.isRequired,
};

export default SearchEntities;
